import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import {
  SLUG_MAX,
  groveShowcaseUrl,
  isValidSlug,
  normalizeSlug,
} from '@goaltree/backend/convex/lib/groveShowcase'
import { withShareUtm } from '@goaltree/backend/convex/lib/shareUtm'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation, useQuery } from 'convex/react'
import { Check, Copy, ExternalLink, Globe, Loader2, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { UserAvatar } from '@/components/user-avatar'
import { optimisticDropJoinRequest, optimisticSetGroveShowcase } from '@/lib/groves-optimistic'

import type { GroveDetail } from './grove-overview'
import { timeAgo, toastGroveError } from './grove-utils'

const label = 'text-xs font-semibold uppercase tracking-wider text-muted-foreground'

export function ShowcaseSettings({ grove }: { grove: GroveDetail }) {
  const { t } = useTranslation()
  const setShowcase = useMutation(api.groveShowcase.setShowcase).withOptimisticUpdate(
    optimisticSetGroveShowcase,
  )
  const saved = grove.showcase
  const [slug, setSlug] = useState(saved?.slug ?? '')
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)
  if (!saved) return null
  const clean = normalizeSlug(slug)
  const valid = isValidSlug(clean)
  const dirty = clean !== saved.slug

  async function save(on: boolean) {
    setBusy(true)
    try {
      await setShowcase({ groveId: grove._id, on, slug: valid ? clean : undefined })
      toast.success(
        dirty && on === saved!.on
          ? t('groves.settings.saved')
          : t(on ? 'groves.showcase.turnedOn' : 'groves.showcase.turnedOff'),
      )
    } catch (err) {
      toastGroveError(err, t)
    } finally {
      setBusy(false)
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(
        withShareUtm(groveShowcaseUrl(saved!.slug), 'grove_showcase'),
      )
      setCopied(true)
      toast.success(t('groves.showcase.linkCopied'))
      setTimeout(() => setCopied(false), 2000)
    } catch {}
  }

  return (
    <section className="space-y-2">
      <p className={label}>{t('groves.showcase.title')}</p>
      <button
        type="button"
        disabled={busy || (!saved.on && !valid)}
        onClick={() => save(!saved.on)}
        className="flex w-full cursor-pointer items-center gap-3 rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted/40 disabled:cursor-default disabled:opacity-60"
      >
        <Globe className="size-4 text-muted-foreground" />
        <span className="flex-1">{t('groves.showcase.toggle')}</span>
        <span
          className={cn(
            'relative h-5 w-9 rounded-full transition-colors',
            saved.on ? 'bg-primary' : 'bg-muted-foreground/30',
          )}
        >
          <span
            className={cn(
              'absolute top-0.5 size-4 rounded-full bg-white transition-[left]',
              saved.on ? 'left-[18px]' : 'left-0.5',
            )}
          />
        </span>
      </button>
      <p className="text-xs text-muted-foreground">{t('groves.showcase.desc')}</p>
      <div className="flex items-center gap-2">
        <div className="flex min-w-0 flex-1 items-center rounded-md border border-border/60 bg-muted/20 px-3 font-mono text-xs">
          <span className="shrink-0 text-muted-foreground">gt.today/g/</span>
          <input
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            maxLength={SLUG_MAX}
            aria-label={t('groves.showcase.slug')}
            spellCheck={false}
            className="min-w-0 flex-1 bg-transparent py-2 text-foreground outline-none"
          />
        </div>
        {dirty ? (
          <button
            type="button"
            disabled={!valid || busy}
            onClick={() => save(saved.on)}
            className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {busy && <Loader2 className="size-3.5 animate-spin" />}
            {t('common.save')}
          </button>
        ) : (
          saved.on && (
            <button
              type="button"
              onClick={copy}
              className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
            >
              {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
              {copied ? t('common.copied') : t('groves.settings.copy')}
            </button>
          )
        )}
      </div>
      {slug && !valid ? (
        <p className="text-xs text-destructive">{t('groves.errors.slugInvalid')}</p>
      ) : saved.on && !dirty ? (
        <a
          href={groveShowcaseUrl(saved.slug)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary-text hover:text-primary-text/80"
        >
          <ExternalLink className="size-3.5" />
          {t('groves.showcase.open')}
        </a>
      ) : null}
    </section>
  )
}

export function JoinRequests({ groveId }: { groveId: Id<'groves'> }) {
  const { t, i18n } = useTranslation()
  const requests = useQuery(api.groveShowcase.listRequests, { groveId })
  const approve = useMutation(api.groveShowcase.approveRequest).withOptimisticUpdate(
    optimisticDropJoinRequest,
  )
  const decline = useMutation(api.groveShowcase.declineRequest).withOptimisticUpdate(
    optimisticDropJoinRequest,
  )
  const [busy, setBusy] = useState<string | null>(null)
  if (!requests?.length) return null

  async function run(id: Id<'groveJoinRequests'>, ok: boolean, name: string) {
    setBusy(id)
    try {
      await (ok ? approve : decline)({ requestId: id })
      if (ok) toast.success(t('groves.requests.approved', { name }))
    } catch (err) {
      toastGroveError(err, t)
    } finally {
      setBusy(null)
    }
  }

  return (
    <section className="rounded-xl border border-primary/30 bg-primary/5 p-3">
      <p className={label}>{t('groves.requests.title', { count: requests.length })}</p>
      <div className="mt-1 divide-y divide-border/40">
        {requests.map((r) => (
          <div key={r._id} className="flex items-start gap-3 py-2.5">
            <UserAvatar
              name={r.name}
              image={r.image}
              className="size-8 shrink-0 rounded-full object-cover"
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-foreground">{r.name}</p>
              {r.message && (
                <p className="mt-0.5 text-xs break-words text-muted-foreground">{r.message}</p>
              )}
              <p className="mt-0.5 text-[11px] text-muted-foreground/70">
                {timeAgo(r.createdAt, i18n.language)}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                disabled={busy === r._id}
                onClick={() => run(r._id, false, r.name)}
                aria-label={t('groves.requests.decline')}
                title={t('groves.requests.decline')}
                className="flex size-8 cursor-pointer items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
              >
                <X className="size-4" />
              </button>
              <button
                type="button"
                disabled={busy === r._id}
                onClick={() => run(r._id, true, r.name)}
                className="flex h-8 cursor-pointer items-center gap-1 rounded-full bg-primary px-3 text-xs font-bold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                {busy === r._id ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Check className="size-3.5" />
                )}
                {t('groves.requests.approve')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
